/**
 * useFieldCoverage — TanStack Query hook для GET /api/v1/ndvi/field-coverage.
 *
 * Возвращает per-field cloud coverage / valid pixel ratio на конкретную сцену
 * (scene_date). Используется FieldDetailPopover (AK-65) для отображения двух
 * блоков: «Облачность сцены» (badge с цветом) и «Среднее по полю … (% clear)».
 *
 * Параметры:
 *   - fieldId: UUID — null отключает запрос (no fetch).
 *   - sceneDate: ISO YYYY-MM-DD — обычно useMapStore.selectedDate.
 *   - enabled: дополнительная защёлка (например `mode !== 'none'`).
 *
 * Endpoint существует с AK-58/59 (см. backend/api/v1/ndvi.py:802) и возвращает:
 *   { valid_ratio: 0..1 | null, cloud_pct: int 0..100 | null, has_data: bool }.
 *
 * Cache: per (field_id, scene_date) — staleTime 5 мин (сцена за прошлую дату
 * не меняется; за текущую — pipeline доливает раз в день).
 *
 * Spec: AK-65 (Контрастный NDVI: cloud-coverage badge + per-field NDVI stats).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface FieldCoverage {
  /** Доля безоблачных пикселей в поле (0..1). null если has_data=false. */
  valid_ratio: number | null;
  /** Облачность сцены в %, округлено до целого. null если has_data=false. */
  cloud_pct: number | null;
  /** False — для этой даты у поля нет записи в field_indices_v2. */
  has_data: boolean;
}

interface UseFieldCoverageArgs {
  fieldId: string | null;
  sceneDate: string | null;
  /** Дополнительная защёлка (mode !== 'none'). По умолчанию true. */
  enabled?: boolean;
}

export function useFieldCoverage({
  fieldId,
  sceneDate,
  enabled = true,
}: UseFieldCoverageArgs): UseQueryResult<FieldCoverage, Error> {
  const isEnabled = enabled && Boolean(fieldId) && Boolean(sceneDate);

  return useQuery<FieldCoverage, Error>({
    queryKey: ['field-coverage', fieldId, sceneDate],
    queryFn: () =>
      api.get<FieldCoverage>(
        `/api/v1/ndvi/field-coverage?field_id=${fieldId}&scene_date=${sceneDate}`,
      ),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Silent fail policy — popup показывает поле без NDVI блоков, но без error UI
    retry: 0,
  });
}
